import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import NewTranscriptionLangDropdown from "../NewStudio/NewTranscriptionLangDropdown";
import {formatToReadableLargeTitle, IMAGE_UPLOAD} from "../../utils/utils";
import {
    formatUnsupportedWorkflowFileMessage,
    getWorkflowUploadFiletype,
} from "./workflowUploadValidation";

const waveformColumns = [18, 34, 26, 40, 22, 30, 38, 24, 32, 20, 36, 18, 26, 34, 28];

const formatFileName = (name = "") => {
    const trimmed = formatToReadableLargeTitle(name || "");
    if (trimmed.length <= 74) return trimmed;
    return `${trimmed.slice(0, 74)}...`;
};

export const formatWorkflowMediaDuration = (durationSec) => {
    if (!Number.isFinite(durationSec) || durationSec <= 0) return "";

    const totalSeconds = Math.max(0, Math.round(durationSec));
    if (totalSeconds < 60) {
        return `${totalSeconds}s`;
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }

    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const AudioWaveformPreview = () => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                borderRadius: "12px",
                background: "linear-gradient(180deg, var(--app-bg-surface) 0%, var(--app-bg-subtle) 100%)",
                border: "1px solid var(--app-border-subtle)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                px: "26px",
            }}
        >
            {waveformColumns.map((height, idx) => (
                <Box
                    key={`${height}-${idx}`}
                    sx={{
                        width: "6px",
                        height: `${height}px`,
                        borderRadius: "999px",
                        backgroundColor: idx % 3 === 0 ? "#2B6BFD" : "var(--app-icon-primary)",
                        opacity: idx % 3 === 0 ? 0.8 : 0.65,
                    }}
                />
            ))}
        </Box>
    );
};

export const ValidationNotice = ({message, severity = "error"}) => {
    if (!message) return null;

    const palette = severity === "warning"
        ? {
            border: "#FCD34D",
            background: "#FFFBEB",
            color: "#92400E",
        }
        : {
            border: "#FECACA",
            background: "#FEF2F2",
            color: "#B91C1C",
        };

    return (
        <Box
            sx={{
                px: "12px",
                py: "10px",
                borderRadius: "10px",
                border: `1px solid ${palette.border}`,
                backgroundColor: palette.background,
                color: palette.color,
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: 500,
                lineHeight: "16px",
            }}
        >
            {message}
        </Box>
    );
};

export const WorkflowUploadShell = ({children}) => {
    return (
        <Box
            sx={{
                width: "715px",
                borderRadius: "12px",
                border: "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-surface)",
                px: "24px",
                py: "24px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
            }}
        >
            {children}
        </Box>
    );
};

export const WorkflowUploadHeader = ({onClose, title, subtitle}) => {
    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                width: "100%",
                gap: "16px",
            }}
        >
            <Box sx={{display: "flex", flexDirection: "column", gap: "4px", pr: "10px"}}>
                <Box
                    sx={{
                        color: "var(--app-text-primary)",
                        fontFamily: "Inter",
                        fontSize: "20px",
                        fontWeight: 600,
                        lineHeight: "28px",
                        letterSpacing: "-0.01em",
                    }}
                >
                    {title}
                </Box>
                <Box
                    sx={{
                        color: "var(--app-text-muted)",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontWeight: 400,
                        lineHeight: "20px",
                    }}
                >
                    {subtitle}
                </Box>
            </Box>
            <Box
                sx={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "10px",
                    border: "1px solid var(--app-border-subtle)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--app-text-muted)",
                    "&:hover": {
                        backgroundColor: "var(--app-bg-subtle)",
                        color: "var(--app-text-primary)",
                    },
                }}
                onClick={onClose}
            >
                <CloseIcon sx={{fontSize: "20px"}}/>
            </Box>
        </Box>
    );
};

const buildDropFiles = (event, {multiple = false} = {}) => {
    const files = [];
    const items = Array.from(event?.dataTransfer?.items || []);
    for (const item of items) {
        if (item?.kind !== "file") continue;
        const file = item.getAsFile?.();
        if (file) files.push(file);
    }

    if (files.length > 0) {
        return multiple ? files : [files[0]];
    }

    const droppedFiles = Array.from(event?.dataTransfer?.files || []);
    if (droppedFiles.length === 0) return [];
    return multiple ? droppedFiles : [droppedFiles[0]];
};

const UploadDropzone = ({
    onChoose,
    validationMessage,
    isDarkMode = false,
    accept,
    uploadTitle,
    uploadHelperText,
    inputId,
    isDragCopyText,
    multiple = false,
    onUseSample,
}) => {
    const inputRef = useRef(null);
    const dragDepthRef = useRef(0);
    const [isDragActive, setIsDragActive] = useState(false);

    const resetDragState = () => {
        dragDepthRef.current = 0;
        setIsDragActive(false);
    };

    const handleChange = async (event) => {
        const chosenFiles = Array.from(event?.target?.files || []);
        if (chosenFiles.length === 0) return;
        await onChoose(multiple ? chosenFiles : chosenFiles[0]);
        if (event?.target) {
            event.target.value = "";
        }
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current += 1;
        setIsDragActive(true);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event?.dataTransfer) {
            event.dataTransfer.dropEffect = "copy";
        }
        if (!isDragActive) {
            setIsDragActive(true);
        }
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
            setIsDragActive(false);
        }
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const droppedFiles = buildDropFiles(event, {multiple});
        resetDragState();
        if (droppedFiles.length === 0) return;
        await onChoose(multiple ? droppedFiles : droppedFiles[0]);
    };

    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "12px"}}>
            <Box
                component="label"
                htmlFor={inputId}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    border: `1px dashed ${isDragActive ? "#2B6BFD" : "var(--app-border-muted)"}`,
                    backgroundColor: isDragActive
                        ? (isDarkMode ? "rgba(43, 107, 253, 0.18)" : "#F3F8FF")
                        : "var(--app-bg-canvas)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                    "&:hover": {
                        backgroundColor: isDarkMode ? "rgba(43, 107, 253, 0.12)" : "var(--app-bg-subtle)",
                        borderColor: "var(--app-border-muted)",
                    },
                }}
            >
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "var(--app-text-primary)",
                        lineHeight: "20px",
                    }}
                >
                    {uploadTitle}
                </Box>
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 400,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                        textAlign: "center",
                        maxWidth: "360px",
                    }}
                >
                    {isDragActive ? isDragCopyText : uploadHelperText}
                </Box>
            </Box>
            <input
                ref={inputRef}
                id={inputId}
                type="file"
                accept={accept}
                multiple={multiple}
                hidden
                onChange={handleChange}
            />
            <ValidationNotice message={validationMessage}/>
            {onUseSample && (
                <Box
                    onClick={onUseSample}
                    sx={{
                        mt: "14px",
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "color 0.15s ease",
                        "&:hover": { color: "var(--app-text-primary)" },
                    }}
                >
                    or try with a{" "}
                    <Box
                        component="span"
                        sx={{
                            color: "#2B6BFD",
                            fontWeight: 600,
                            "&:hover": { textDecoration: "underline" },
                        }}
                    >
                        sample recording
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export const SingleFileSelectState = ({onFileChoose, ...props}) => {
    return (
        <UploadDropzone
            onChoose={onFileChoose}
            multiple={false}
            isDragCopyText="Drop file to upload."
            {...props}
        />
    );
};

export const MultiFileSelectState = (props) => {
    return (
        <UploadDropzone
            multiple={true}
            isDragCopyText="Drop files to upload."
            {...props}
        />
    );
};

export const MediaPreviewCard = ({file, showMeta = false}) => {
    const [previewUrl, setPreviewUrl] = useState("");
    const [aspectRatio, setAspectRatio] = useState("landscape");
    const [durationLabel, setDurationLabel] = useState("");

    const isVideo = Boolean(file?.type?.startsWith("video/"));
    const isAudio = Boolean(file?.type?.startsWith("audio/"));
    const isImage = Boolean(file?.type?.startsWith("image/"));

    useEffect(() => {
        if (!isVideo || !file) {
            setPreviewUrl("");
            setAspectRatio("landscape");
            if (!isAudio) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const videoUrl = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.src = videoUrl;
        video.preload = "metadata";
        video.muted = true;

        const cleanupVideoUrl = () => {
            URL.revokeObjectURL(videoUrl);
        };

        video.onloadedmetadata = () => {
            if (cancelled) return;
            const ratio = (video.videoWidth || 1) / (video.videoHeight || 1);
            if (ratio > 1.1) setAspectRatio("landscape");
            else if (ratio < 0.9) setAspectRatio("portrait");
            else setAspectRatio("square");
            setDurationLabel(formatWorkflowMediaDuration(video.duration));

            const seekTarget = Number.isFinite(video.duration) && video.duration > 1 ? 1 : 0;
            const safeTarget = Math.max(0, Math.min(seekTarget, (video.duration || 0) - 0.05));
            video.currentTime = Number.isFinite(safeTarget) ? safeTarget : 0;
        };

        video.onseeked = () => {
            if (cancelled) return;
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth || 1280;
            canvas.height = video.videoHeight || 720;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                cleanupVideoUrl();
                return;
            }
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            setPreviewUrl(canvas.toDataURL("image/jpeg", 0.85));
            cleanupVideoUrl();
        };

        video.onerror = () => {
            cleanupVideoUrl();
        };

        return () => {
            cancelled = true;
            cleanupVideoUrl();
        };
    }, [file, isVideo]);

    useEffect(() => {
        if (!isAudio || !file) {
            if (!isVideo) {
                setDurationLabel("");
            }
            return;
        }

        let cancelled = false;
        const audioUrl = URL.createObjectURL(file);
        const audio = document.createElement("audio");
        audio.src = audioUrl;
        audio.preload = "metadata";

        audio.onloadedmetadata = () => {
            if (cancelled) return;
            setDurationLabel(formatWorkflowMediaDuration(audio.duration));
            URL.revokeObjectURL(audioUrl);
        };

        audio.onerror = () => {
            if (cancelled) return;
            setDurationLabel("");
            URL.revokeObjectURL(audioUrl);
        };

        return () => {
            cancelled = true;
            URL.revokeObjectURL(audioUrl);
        };
    }, [file, isAudio, isVideo]);

    useEffect(() => {
        if (!isImage || !file) {
            return;
        }

        const imageUrl = URL.createObjectURL(file);
        setPreviewUrl(imageUrl);
        setDurationLabel("");

        return () => {
            URL.revokeObjectURL(imageUrl);
        };
    }, [file, isImage]);

    const mediaStyle = (() => {
        if (aspectRatio === "portrait" || aspectRatio === "square") {
            return {
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
            };
        }
        return {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        };
    })();

    const metaText = [formatFileName(file?.name), durationLabel].filter(Boolean).join(" \u2022 ");

    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "8px"}}>
            <Box
                sx={{
                    width: "100%",
                    height: "300px",
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid var(--app-border-subtle)",
                    backgroundColor: "var(--app-bg-subtle)",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {isVideo && previewUrl ? (
                    <img src={previewUrl} alt="Uploaded video preview" style={mediaStyle}/>
                ) : null}
                {isImage && previewUrl ? (
                    <img
                        src={previewUrl}
                        alt="Uploaded image preview"
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                        }}
                    />
                ) : null}
                {isAudio ? <AudioWaveformPreview/> : null}
            </Box>
            {showMeta && metaText ? (
                <Box
                    sx={{
                        fontFamily: "Inter",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "var(--app-text-muted)",
                        lineHeight: "18px",
                        textAlign: "left",
                    }}
                >
                    {metaText}
                </Box>
            ) : null}
        </Box>
    );
};

const ContentLanguageCard = ({
    selectedLanguage,
    setSelectedLanguage,
    description = "Set language for speech transcription quality.",
}) => {
    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: "12px",
                border: "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-surface)",
                px: "14px",
                py: "14px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
            }}
        >
            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "var(--app-text-primary)",
                    lineHeight: "18px",
                }}
            >
                Content language
            </Box>
            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "12px",
                    fontWeight: 400,
                    color: "var(--app-text-muted)",
                    lineHeight: "18px",
                }}
            >
                {description}
            </Box>
            <Box sx={{width: "234px"}}>
                <NewTranscriptionLangDropdown
                    selectedLanguage={selectedLanguage}
                    onChange={setSelectedLanguage}
                    compact={true}
                />
            </Box>
        </Box>
    );
};

const SubmitButton = ({label, isSubmitting, onClick}) => {
    return (
        <Box
            sx={{
                width: "100%",
                height: "52px",
                borderRadius: "999px",
                backgroundColor: "#1a1a1a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isSubmitting ? "default" : "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": {
                    backgroundColor: isSubmitting ? "#1a1a1a" : "#111827",
                },
                opacity: isSubmitting ? 0.8 : 1,
            }}
            onClick={isSubmitting ? undefined : onClick}
        >
            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#FFFFFF",
                    lineHeight: "20px",
                    letterSpacing: "0.01em",
                }}
            >
                {label}
            </Box>
        </Box>
    );
};

const SwapFileButton = ({label, onClick}) => {
    return (
        <Box
            sx={{
                width: "fit-content",
                fontFamily: "Inter",
                fontSize: "13px",
                fontWeight: 500,
                color: "var(--app-text-muted)",
                cursor: "pointer",
                "&:hover": {
                    color: "var(--app-text-primary)",
                },
            }}
            onClick={onClick}
        >
            {label}
        </Box>
    );
};

export const SingleFileReviewState = ({
    selectedFile,
    selectedLanguage,
    setSelectedLanguage,
    onSubmit,
    onSwapFile,
    validationMessage,
    isSubmitting,
    submitLabel = "Upload",
    languageDescription,
}) => {
    const isImageUpload = getWorkflowUploadFiletype(selectedFile) === IMAGE_UPLOAD;

    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "16px"}}>
            <MediaPreviewCard file={selectedFile} showMeta={true}/>

            {!isImageUpload ? (
                <ContentLanguageCard
                    selectedLanguage={selectedLanguage}
                    setSelectedLanguage={setSelectedLanguage}
                    description={languageDescription}
                />
            ) : null}

            <ValidationNotice message={validationMessage}/>
            <SubmitButton
                label={isSubmitting ? "Preparing upload..." : submitLabel}
                isSubmitting={isSubmitting}
                onClick={onSubmit}
            />
            <SwapFileButton label="Choose a different file" onClick={onSwapFile}/>
        </Box>
    );
};

const SelectedFilesListCard = ({files}) => {
    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: "14px",
                border: "1px solid var(--app-border-subtle)",
                backgroundColor: "var(--app-bg-subtle)",
                px: "16px",
                py: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
            }}
        >
            <Box
                sx={{
                    fontFamily: "Inter",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "var(--app-text-primary)",
                    lineHeight: "18px",
                }}
            >
                {files.length} file{files.length === 1 ? "" : "s"} selected
            </Box>
            <Box sx={{display: "flex", flexDirection: "column", gap: "8px"}}>
                {files.map((entry, index) => {
                    const file = entry?.file || entry;
                    const durationLabel = formatWorkflowMediaDuration(Number(entry?.durationSec ?? 0));
                    return (
                        <Box
                            key={`${file?.name || "file"}-${index}`}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: 0,
                                    fontFamily: "Inter",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "var(--app-text-primary)",
                                    lineHeight: "18px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {formatFileName(file?.name || "")}
                            </Box>
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    fontFamily: "Inter",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "var(--app-text-muted)",
                                    lineHeight: "16px",
                                }}
                            >
                                {durationLabel || " "}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export const MultiFileReviewState = ({
    files,
    selectedLanguage,
    setSelectedLanguage,
    onSubmit,
    onSwapFiles,
    validationMessage,
    warningMessage,
    isSubmitting,
    submitLabel = "Upload",
    languageDescription,
}) => {
    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: "16px"}}>
            <SelectedFilesListCard files={files}/>
            <ContentLanguageCard
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                description={languageDescription}
            />
            <ValidationNotice message={warningMessage} severity="warning"/>
            <ValidationNotice message={validationMessage}/>
            <SubmitButton
                label={isSubmitting ? "Preparing uploads..." : submitLabel}
                isSubmitting={isSubmitting}
                onClick={onSubmit}
            />
            <SwapFileButton label="Choose different files" onClick={onSwapFiles}/>
        </Box>
    );
};

export {
    formatFileName as formatWorkflowFileName,
    formatUnsupportedWorkflowFileMessage,
    getWorkflowUploadFiletype,
};
